import { useEffect, useMemo, useState } from "react"

import * as turf from "@turf/turf"
import { useParams } from "react-router-dom"

import { useGeoServerFeaturesList } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { MAP_TYPE_ROAD, useMapContext } from "@l2r-front/l2r-map"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useApplicationStateContext, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { CANVAS_MIN_BBOX_WIDTH } from "../../constants/bboxMinWidth"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { useNetworksStateContext } from "../../contexts/NetworksContext"
import { networksQueryKeys } from "../../hooks"

export const MapAdapterRoad = () => {

    const { t } = useTranslation()
    const { setSnackbar } = useAlertsDispatchContext()
    const navigate = useNavigateWithSearchParams()
    const { road } = useParams()
    const [mapState, mapDispatch] = useMapContext()
    const { getMapRef, resetMapBoundingBox, setMapType, setMapBoundingBoxToFit, restoreMapBoundingBox } = mapDispatch
    const { storedBoundingBox } = mapState
    const mapRef = getMapRef()
    const { selectedNetwork } = useNetworksStateContext()
    const { navigationOrigin } = useApplicationStateContext()
    const [isBBSet, setIsBBSet] = useState(false)
    const isMobile = useIsMobileDevice()

    const queryKeyParams = { road }
    const { data: roadSegments, isError, isLoading, refetch } = useGeoServerFeaturesList(
        networksQueryKeys.list(queryKeyParams),
        {
            layer: selectedNetwork?.referential?.layerSection,
            linear_location_road: road,
        },
        {
            enabled: !!road && !!selectedNetwork?.referential,
        })

    useEffect(() => {
        setMapType(MAP_TYPE_ROAD)
    }, [setMapType])

    useEffect(() => {
        if (road && selectedNetwork) {
            setIsBBSet(false)
            refetch()
        }
    }, [road, selectedNetwork, refetch])

    const roadBoundingBox = useMemo(() => {
        if (isLoading) {
            return null
        }
        if (!roadSegments || !roadSegments.length) {
            const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
            return {
                minLng,
                minLat,
                maxLng,
                maxLat,
            }
        }

        const roadFeature = turf.featureCollection(roadSegments)
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(roadFeature)
        const lngOffset = Math.max((CANVAS_MIN_BBOX_WIDTH - (maxLng - minLng)), 0) / 2
        return ({
            minLng: minLng - lngOffset,
            minLat,
            maxLng: maxLng + lngOffset,
            maxLat,
        })
    }, [roadSegments, selectedNetwork, isLoading])

    useEffect(() => {
        if (!isLoading && (!roadSegments?.length || isError)) {
            setSnackbar({
                autoHideDuration: 5000,
                label: t(I18N_NAMESPACE, "containers.mapAdapterSegment.error"),
                severity: "error",
                anchorOrigin: {
                    horizontal: "left",
                    vertical: "bottom",
                },
            })

            return navigate("..")
        }
        return
    }, [roadSegments, isError, isLoading, navigate, setSnackbar, t])

    useEffect(() => {
        if (mapRef && !isBBSet) {
            if (storedBoundingBox.road) {
                restoreMapBoundingBox(MAP_TYPE_ROAD)
                setIsBBSet(true)
            } else if (roadBoundingBox) {
                const map = mapRef.getMap()
                const newCameraWithBounds = map.cameraForBounds([
                    [roadBoundingBox.minLng, roadBoundingBox.minLat],
                    [roadBoundingBox.maxLng, roadBoundingBox.maxLat],
                ])

                switch (navigationOrigin) {
                    case NavigationOrigins.ROAD_FROM_DETAIL:
                        break
                    case NavigationOrigins.ROAD_FROM_ROAD:
                        break
                    case NavigationOrigins.ROAD_FROM_NETWORK:
                        if (isMobile) {
                            const previousZoom = storedBoundingBox?.network?.zoom
                            if (!previousZoom || newCameraWithBounds.zoom > storedBoundingBox.network.zoom) {
                                setMapBoundingBoxToFit(roadBoundingBox)
                            } else {
                                setMapBoundingBoxToFit(storedBoundingBox.network)
                            }
                        } else if (newCameraWithBounds?.zoom > map.getZoom()) {
                            setMapBoundingBoxToFit(roadBoundingBox)
                        }
                        break
                    case NavigationOrigins.ROAD_FROM_BACK_DETAIL:
                        break
                    case NavigationOrigins.ROAD_FROM_SEARCH:
                        setMapBoundingBoxToFit(roadBoundingBox)
                        break
                    case NavigationOrigins.NONE:
                        setMapBoundingBoxToFit(roadBoundingBox)
                        break
                }
                resetMapBoundingBox()
                return setIsBBSet(true)
            }
        } else if (!mapRef) {
            setIsBBSet(false)
        }
    }, [isMobile,
        mapRef,
        navigationOrigin,
        resetMapBoundingBox,
        roadBoundingBox,
        setMapBoundingBoxToFit,
        storedBoundingBox,
        restoreMapBoundingBox,
        isBBSet,
        setIsBBSet,
    ])

    return null
}